import { useState, useEffect } from "react";
import { validateBirthday } from "./validators";

type BirthdaySelectProps = {
  attr: any;
  personalInfo: any;
  setPersonalInfo: React.Dispatch<React.SetStateAction<any>>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
};

export const BirthdaySelect = ({
  attr,
  personalInfo,
  setPersonalInfo,
  setErrors,
}: BirthdaySelectProps) => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => 1900 + i);

  const [selectedYear, setSelectedYear] = useState<number | "">(1900);
  const [selectedMonth, setSelectedMonth] = useState<number | "">(1);
  const [selectedDay, setSelectedDay] = useState<number | "">(1);

  // バリデーション有効化フラグ
  const [validationEnabled, setValidationEnabled] = useState(false);

  // 月ごとの日数
  const getDaysInMonth = (year: number, month: number) =>
    new Date(year, month, 0).getDate();

  // 月リスト（未来月を除外）
  const months =
    selectedYear && Number(selectedYear) === currentYear
      ? Array.from({ length: currentMonth }, (_, i) => i + 1)
      : Array.from({ length: 12 }, (_, i) => i + 1);

  // 日付リスト（未来日を除外）   
  const days =
    selectedYear && selectedMonth
      ? Array.from(
          { length: getDaysInMonth(Number(selectedYear), Number(selectedMonth)) },
          (_, i) => i + 1
        ).filter((d) => {
          if (Number(selectedYear) > currentYear) return false;
          if (
            Number(selectedYear) === currentYear &&
            Number(selectedMonth) > currentMonth
          )
            return false;
          if (
            Number(selectedYear) === currentYear &&
            Number(selectedMonth) === currentMonth &&
            d > currentDay
          )
            return false;
          return true;
        })
      : Array.from({ length: 31 }, (_, i) => i + 1);

  // 初期値復元
  useEffect(() => {
    let value =
      personalInfo?.attributes?.[attr.name]?.[0] ??
      personalInfo?.[attr.name];

    if (Array.isArray(value)) value = value[0];

    let y = 1900;
    let m = 1;
    let d = 1;

    if (value && value.length === 8) {
      const yy = Number(value.substring(0, 4));
      const mm = Number(value.substring(4, 6));
      const dd = Number(value.substring(6, 8));

      if (!Number.isNaN(yy)) y = yy;
      if (!Number.isNaN(mm)) m = mm;
      if (!Number.isNaN(dd)) d = dd;
    }

    setSelectedYear(y);
    setSelectedMonth(m);
    setSelectedDay(d);

    setValidationEnabled(false);

    setErrors(prev => ({ ...prev, [attr.name]: null }));
  }, [personalInfo]);

  // バリデーション
  useEffect(() => {
    if (!validationEnabled) return;

    const raw =
      selectedYear && selectedMonth && selectedDay
        ? `${selectedYear}${String(selectedMonth).padStart(2, "0")}${String(selectedDay).padStart(2, "0")}`
        : "";

    const result = validateBirthday(raw);

    let errorKey: string | null = null;

    if (result === "required") {
      errorKey = "screen-valid.error-user-attribute-required-birthday";
    } else if (result === "invalid") {
      errorKey = "screen-valid.error-pattern-not_exist-date";
    }

    // キーを保存
    setErrors(prev => ({ ...prev, [attr.name]: errorKey }));
  }, [selectedYear, selectedMonth, selectedDay, validationEnabled]);

  const updatePersonalInfo = (year: number, month: number, day: number) => {
    if (!(year === 1900 && month === 1 && day === 1)) {
      const birthday =
        `${year}` +
        `${String(month).padStart(2, "0")}` +
        `${String(day).padStart(2, "0")}`;

      setPersonalInfo((prev:any) => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          [attr.name]: [birthday],
        },
      }));
    } else {
      setPersonalInfo((prev:any) => {
        const newAttrs = { ...prev.attributes };
        delete newAttrs[attr.name];
        return { ...prev, attributes: newAttrs };
      });
    }
  };

  return (
    <div style={{ display: "flex", gap: "8px" }}>
      {/* 年 */}
      <select
        value={selectedYear}
        onChange={(e) => {
          const y = Number(e.target.value);
          setSelectedYear(y);
          setValidationEnabled(true);

          if (!days.includes(Number(selectedDay))) {
            setSelectedDay("");
          }

          updatePersonalInfo(y, Number(selectedMonth), Number(selectedDay));
        }}
        className={"nc-select-birthday"}
        id={"select_year"}
      >
        {selectedYear === "" && <option value=""></option>}
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>

      {/* 月 */}
      <select
        value={selectedMonth}
        onChange={(e) => {
          const m = Number(e.target.value);
          setSelectedMonth(m);
          setValidationEnabled(true);

          if (!days.includes(Number(selectedDay))) {
            setSelectedDay("");
          }

          updatePersonalInfo(Number(selectedYear), m, Number(selectedDay));
        }}
        className={"nc-select-birthday"}
        id={"select_month"}
      >
        {selectedMonth === "" && <option value=""></option>}
        {months.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>

      {/* 日 */}
      <select
        value={selectedDay}
        onChange={(e) => {
          const d = Number(e.target.value);
          setSelectedDay(d);
          setValidationEnabled(true);
          updatePersonalInfo(Number(selectedYear), Number(selectedMonth), d);
        }}
        className={"nc-select-birthday"}
        id={"select_day"}
      >
        {selectedDay === "" && <option value=""></option>}

        {(days.length === 0
          ? Array.from({ length: currentDay }, (_, i) => i + 1)
          : days
        ).map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>
    </div>
  );
};